// src/components/common/NotificationBell.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bell, X, Check, CheckCheck, Trash2,
  ShieldCheck, CheckCircle, XCircle, Radio, Lock,
  CreditCard, User, Banknote, AlertTriangle, Wifi, WifiOff,
} from 'lucide-react';
import {
  getNotifications, markRead, markAllRead, deleteNotification, clearAll,
} from '../../services/notificationService';

// Map icon key strings (from backend notify.js) to Lucide components + colors
const ICON_MAP = {
  'bell':         { Icon: Bell,          color: '#60a5fa' },
  'hand-wave':    { Icon: Bell,          color: '#34d399' },
  'check-circle': { Icon: CheckCircle,   color: '#34d399' },
  'shield-check': { Icon: ShieldCheck,   color: '#34d399' },
  'x-circle':     { Icon: XCircle,       color: '#f87171' },
  'radio':        { Icon: Radio,         color: '#60a5fa' },
  'lock':         { Icon: Lock,          color: '#a78bfa' },
  'credit-card':  { Icon: CreditCard,    color: '#a78bfa' },
  'user':         { Icon: User,          color: '#60a5fa' },
  'banknote':     { Icon: Banknote,      color: '#34d399' },
  'alert':        { Icon: AlertTriangle, color: '#fbbf24' },
  'check':        { Icon: CheckCircle,   color: '#34d399' },
  'device_offline':  { Icon: WifiOff,   color: '#f87171' },
  'device_online':   { Icon: Wifi,      color: '#34d399' },
  'gateway_offline': { Icon: WifiOff,   color: '#f87171' },
  'gateway_online':  { Icon: Wifi,      color: '#34d399' },
};

const NotifIcon = ({ icon, type }) => {
  const key = icon || type || 'bell';
  const match = ICON_MAP[key] || ICON_MAP['bell'];
  const { Icon, color } = match;
  return <Icon style={{ width: 18, height: 18, color }} />;
};
const POLL_INTERVAL = 60_000; // 60 seconds

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${d}d ago`;
};

export default function NotificationBell({ collapsed, onNavigate }) {
  const [open,         setOpen]         = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread,       setUnread]       = useState(0);
  const [loading,      setLoading]      = useState(false);
  const panelRef  = useRef(null);
  const pollRef   = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const res = await getNotifications({ limit: 30 });
      if (res.success) {
        setNotifications(res.notifications || []);
        setUnread(res.unreadCount ?? 0);
      }
    } catch { /* ignore */ }
  }, []);

  // Initial load + polling
  useEffect(() => {
    fetchAll();
    pollRef.current = setInterval(fetchAll, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [fetchAll]);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    setOpen(o => !o);
    if (!open) fetchAll(); // refresh on open
  };

  const handleMarkRead = async (id) => {
    await markRead(id).catch(() => {});
    setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllRead = async () => {
    setLoading(true);
    await markAllRead().catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
    setLoading(false);
  };

  const handleDelete = async (id, wasUnread) => {
    await deleteNotification(id).catch(() => {});
    setNotifications(prev => prev.filter(n => n._id !== id));
    if (wasUnread) setUnread(prev => Math.max(0, prev - 1));
  };

  const handleClearAll = async () => {
    setLoading(true);
    await clearAll().catch(() => {});
    setNotifications([]);
    setUnread(0);
    setLoading(false);
  };

  const handleNotificationClick = async (n) => {
    if (!n.read) await handleMarkRead(n._id);
    if (n.link && onNavigate) {
      onNavigate(n.link);
      setOpen(false);
    }
  };

  return (
    <div style={{ position: 'relative' }} ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={handleOpen}
        title="Notifications"
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 12, padding: collapsed ? '10px 8px' : '10px 12px',
          background: open ? 'rgba(255,255,255,0.1)' : 'transparent',
          border: 'none', borderRadius: 8, cursor: 'pointer',
          color: '#9ca3af', transition: 'background 0.15s',
          position: 'relative',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
        onMouseLeave={e => e.currentTarget.style.background = open ? 'rgba(255,255,255,0.1)' : 'transparent'}
      >
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Bell style={{ width: collapsed ? 20 : 16, height: collapsed ? 20 : 16 }} />
          {unread > 0 && (
            <span style={{
              position: 'absolute', top: -5, right: -5,
              background: '#ef4444', color: '#fff',
              fontSize: 9, fontWeight: 800, lineHeight: 1,
              minWidth: 16, height: 16, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 3px', border: '1.5px solid #111827',
            }}>
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </div>
        {!collapsed && (
          <span style={{ fontSize: 14, fontWeight: 500, color: '#d1d5db' }}>Notifications</span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'fixed',
          top: 0, bottom: 0,
          left: collapsed ? 64 : 240,
          width: 360,
          background: '#1f2937',
          borderRight: '1px solid #374151',
          zIndex: 500,
          display: 'flex', flexDirection: 'column',
          boxShadow: '4px 0 24px rgba(0,0,0,0.4)',
        }}>
          {/* Header */}
          <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #374151', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Bell style={{ width: 16, height: 16, color: '#9ca3af' }} />
                <span style={{ fontSize: 15, fontWeight: 700, color: '#f9fafb' }}>Notifications</span>
                {unread > 0 && (
                  <span style={{ background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 6px', borderRadius: 10 }}>
                    {unread} new
                  </span>
                )}
              </div>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6b7280', borderRadius: 6 }}>
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            {notifications.length > 0 && (
              <div style={{ display: 'flex', gap: 8 }}>
                {unread > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    disabled={loading}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 10px', background: '#065f46', border: 'none', borderRadius: 7, cursor: 'pointer', color: '#fff', fontSize: 11, fontWeight: 700 }}
                  >
                    <CheckCheck style={{ width: 12, height: 12 }} /> Mark all read
                  </button>
                )}
                <button
                  onClick={handleClearAll}
                  disabled={loading}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '6px 10px', background: '#374151', border: 'none', borderRadius: 7, cursor: 'pointer', color: '#9ca3af', fontSize: 11, fontWeight: 700 }}
                >
                  <Trash2 style={{ width: 12, height: 12 }} /> Clear all
                </button>
              </div>
            )}
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <Bell style={{ width: 32, height: 32, color: '#374151', display: 'block', margin: '0 auto 12px' }} />
                <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n._id}
                  style={{
                    display: 'flex', gap: 10, padding: '12px 14px',
                    borderBottom: '1px solid #374151',
                    background: n.read ? 'transparent' : 'rgba(6,95,70,0.1)',
                    cursor: n.link ? 'pointer' : 'default',
                    transition: 'background 0.1s',
                    alignItems: 'flex-start',
                  }}
                  onClick={() => handleNotificationClick(n)}
                  onMouseEnter={e => e.currentTarget.style.background = n.read ? '#1e2a38' : 'rgba(6,95,70,0.18)'}
                  onMouseLeave={e => e.currentTarget.style.background = n.read ? 'transparent' : 'rgba(6,95,70,0.1)'}
                >
                  {/* Icon */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: n.read ? '#1e2a38' : '#0d2137',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <NotifIcon icon={n.icon} type={n.type} />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: n.read ? 500 : 700, color: n.read ? '#d1d5db' : '#f9fafb', lineHeight: 1.4 }}>
                      {n.title}
                    </p>
                    <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                      {n.message}
                    </p>
                    <p style={{ margin: '5px 0 0', fontSize: 10, color: '#4b5563', fontWeight: 600 }}>
                      {timeAgo(n.createdAt)}
                    </p>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                    {!n.read && (
                      <button
                        onClick={e => { e.stopPropagation(); handleMarkRead(n._id); }}
                        title="Mark as read"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, color: '#10b981', borderRadius: 4 }}
                      >
                        <Check style={{ width: 13, height: 13 }} />
                      </button>
                    )}
                    <button
                      onClick={e => { e.stopPropagation(); handleDelete(n._id, !n.read); }}
                      title="Delete"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, color: '#4b5563', borderRadius: 4 }}
                      onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                      onMouseLeave={e => e.currentTarget.style.color = '#4b5563'}
                    >
                      <X style={{ width: 12, height: 12 }} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
